import { useEffect, useCallback, useMemo } from 'react';
import { useLanguageContext } from '../../../../../contexts/LanguageContext';
import Dialog from '../../../../../components/Dialog';
import { ActionButton } from '../../../../../components/Button';
import { Box, Grid } from '@mui/material';
import DefaultConstants from '../../../../../data/Constants';
import { IDefaultDialogFilterProps } from '../../../../../interfaces/IReport';
import DateTimeHelper from '../../../../../helpers/DateTimeHelper';
import DateField from '../../../../../components/DateField';
import SelectField from '../../../../../components/SelectField';
import { useAuthContext } from '../../../../../contexts/AuthContext';
import IEnterprise from '../../../../../interfaces/IEnterprise';
import IAccessProfile from '../../../../../interfaces/IAccessProfile';
import Switch from '../../../../../components/Switch';
import useState from '../../../../../hooks/useState';


export interface IStatisticalLogReportFilters {
    enterprise: IEnterprise | undefined;
    accessProfile: IAccessProfile[];
    startDate: string | undefined;
    endDate: string | undefined;
    hideZeroedUsers: boolean;
}


interface IStatisticalLogReportDialogFilterProps extends IDefaultDialogFilterProps {
    accessProfileList: IAccessProfile[];
}

const StatisticalLogFilters: React.FunctionComponent<IStatisticalLogReportDialogFilterProps> = ({
    open, 
    handleClose,
    handleOpenPreview,
    enterpriseList,
    accessProfileList
}) => {
    const { translate } = useLanguageContext();
    const { enterprise } = useAuthContext();
    
    const state = useState<IStatisticalLogReportFilters>({
        enterprise: enterprise,
        accessProfile: [],
        startDate: DateTimeHelper.Now('YYYY-MM-DD'),
        endDate: DateTimeHelper.Now('YYYY-MM-DD'),
        hideZeroedUsers: false
    });


    useEffect(() => {
        if (open) {
            state.reset();
        }
        // eslint-disable-next-line
    }, [open]);


    useEffect(() => {
        state.update('accessProfile', []);
        // eslint-disable-next-line
    }, [state.values.enterprise]);


    const dialogActions = useMemo(() => {
        return [
            <ActionButton action='close' onClick={handleClose} />,
            <ActionButton action='view' type='submit' />
        ];
        // eslint-disable-next-line
    }, [translate]);


    const handleSubmit = useCallback((event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const _filters = state.values;
        const startDate = _filters.startDate;
        const endDate = _filters.endDate;
        
        if (startDate && endDate && startDate > endDate) {
            const aux = startDate;
            _filters.startDate = endDate;
            _filters.endDate = aux;
        }

        handleOpenPreview(_filters);

        state.update('startDate', _filters.startDate);
        state.update('endDate', _filters.endDate);
        // eslint-disable-next-line
    }, [state.values]);


    return (
        <Dialog 
            open={open}
            handleClose={handleClose}
            title={translate('statisticalLog')}
            fullWidth={true}
            dialogActions={dialogActions}
            onSubmit={handleSubmit}
            className='dialogactions-spacebetween'
            maxWidth='md'
            fullHeight={true}
        >
            <Grid container rowSpacing={DefaultConstants.gridRowSpacing} columnSpacing={DefaultConstants.gridColumnSpacing}>
                <Grid item lg={6} md={6} sm={12} xs={12}>
                    <SelectField
                        label={translate('enterprise')}
                        name='enterprise'
                        value={state.values.enterprise?.id ?? 0}
                        onChange={e => {
                            const { name, value } = e.target;
                            const enterprise = enterpriseList.find(e => +e.id === +value);
                            state.update(name, enterprise);
                        }}
                        required={true}
                        data={enterpriseList.map(e => {
                            return {
                                value: +e.id,
                                text: e.name
                            };
                        })}
                    />
                </Grid>

                <Grid item lg={3} md={3} sm={6} xs={6}>
                    <DateField
                        label={translate('startDate')}
                        format='DD/MM/YYYY'
                        name='startDate'
                        value={DateTimeHelper.ToDate(state.values.startDate)}
                        onChange={value => state.update('startDate', DateTimeHelper.Format(value, 'YYYY-MM-DD'))}
                        required={true}
                        disableFuture={true}
                    />
                </Grid>

                <Grid item lg={3} md={3} sm={6} xs={6}>
                    <DateField
                        label={translate('endDate')}
                        format='DD/MM/YYYY'
                        name='endDate'
                        value={DateTimeHelper.ToDate(state.values.endDate)}
                        onChange={value => state.update('endDate', DateTimeHelper.Format(value, 'YYYY-MM-DD'))}
                        required={true}
                        disableFuture={true}
                    />
                </Grid>

                <Grid item xs={12} />

                <Grid item lg={4} md={4} sm={6} xs={12}>
                    <SelectField
                        label={translate('accessProfile')}
                        name='accessProfile'
                        value={state.values.accessProfile.length > 0 ? state.values.accessProfile.map(a => +a.id) : [-1]}
                        required={false}
                        multiple={true}
                        onChange={e => {
                            const { name, value } = e.target;
                            const values = value as number[];

                            const accessProfile = state.values.accessProfile.length > 0 && values.includes(-1) ? [] : accessProfileList.filter(a => values.includes(+a.id));
                            state.update(name, accessProfile);
                        }}
                        data={[{ value: -1, text: translate('all') }].concat(accessProfileList.map(a => {
                            return {
                                value: +a.id,
                                text: a.description
                            };
                        }))}
                    />
                </Grid>

                <Grid item xs={12}>
                    <Box padding={2}>
                        <Switch
                            label={translate('_hideZeroedUsers')}
                            labelPlacement='end'
                            name='hideZeroedUsers'
                            value={state.values.hideZeroedUsers ? 0 : 1}
                            checked={state.values.hideZeroedUsers}
                            onChange={e => state.update(e.target.name, Boolean(+e.target.value))}
                        />
                    </Box>
                </Grid>
            </Grid>
        </Dialog>
    );
}

export default StatisticalLogFilters;